// A destructive action asks first (RizzUI Modal, as in the Secrets delete dialog); so does a
// change that reaches further than it looks (`tone: "primary"`, e.g. allowing a domain for every
// group on a list). When the API refuses (a 409 "in use"), the dialog stays open and says why.
import { CheckCircle, Trash } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";
import { Button, Modal } from "rizzui";

import { useT } from "../i18n";
import { ErrorState } from "./states";

interface Props {
  open: boolean;
  title: ReactNode;
  /** The accessible name, when the title is not plain text. */
  label: string;
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
  /** Red with a bin (the default) for what destroys; the brand colour for a far-reaching change. */
  tone?: "danger" | "primary";
}

export function ConfirmDialog({ open, title, label, children, confirmLabel, onConfirm, onClose, tone = "danger" }: Props) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const [refused, setRefused] = useState<string>();
  const close = () => {
    setRefused(undefined);
    onClose();
  };
  async function confirm() {
    setBusy(true);
    setRefused(undefined);
    try {
      await onConfirm();
      close();
    } catch (e) {
      setRefused(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal isOpen={open} onClose={close} containerClassName="dark:bg-gray-100">
      <div className="p-6" role="group" aria-label={label}>
        <div className="flex items-start gap-3">
          <span className={tone === "danger" ? "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-lighter text-red-dark" : "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-lighter text-primary-dark"}>
            {tone === "danger" ? <Trash weight="duotone" className="h-5 w-5" /> : <CheckCircle weight="duotone" className="h-5 w-5" />}
          </span>
          <div className="min-w-0">
            <h2 className="text-lg">{title}</h2>
            <div className="mt-1 text-[13px] text-gray-500">{children}</div>
          </div>
        </div>
        {refused ? <ErrorState className="mt-4" title={t.common.states.refused} message={refused} /> : null}
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="outline" onClick={close} disabled={busy}>
            {refused ? t.common.actions.close : t.common.actions.cancel}
          </Button>
          <Button color={tone} isLoading={busy} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
